import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';

/**
 * Menu item configuration for the dropdown menu component.
 */
export type DropdownMenuItemType = {
  readonly label: string;
  readonly icon?: string;
  readonly disabled?: boolean;
  readonly separator?: boolean;
  readonly class?: string;
  readonly shortcut?: string;
  readonly command?: () => void;
  readonly routerLink?: string | string[];
};

/**
 * Generic dropdown menu component.
 * Supports command callbacks, router navigation, icons, and keyboard dismissal.
 */
@Component({
  selector: 'app-dropdown-menu',
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'close()',
  },
  template: `
    <div class="relative inline-block text-left" #container>
      <div
        (click)="toggle()"
        (keydown.enter)="toggle()"
        (keydown.space)="onTriggerSpace($event)"
        role="button"
        [attr.tabindex]="disabled() ? null : 0"
        aria-haspopup="menu"
        [attr.aria-expanded]="isOpen()"
        [attr.aria-disabled]="disabled() ? 'true' : null"
        class="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark dark:focus-visible:ring-white rounded"
      >
        <ng-content />
      </div>

      @if (isOpen()) {
        <div
          #panel
          role="menu"
          [class]="panelClasses()"
          [style.transform-origin]="transformOrigin()"
        >
          <div class="py-1">
            @if (items().length > 0) {
              @for (item of items(); track $index) {
                @if (item.separator) {
                  <div class="my-1 h-px bg-gray-200 dark:bg-dark-grey" role="separator"></div>
                } @else {
                  <button
                    type="button"
                    role="menuitem"
                    [disabled]="item.disabled"
                    [class]="getItemClasses(item)"
                    (click)="handleItemClick(item)"
                  >
                    @if (item.icon) {
                      <span [class]="getIconClasses(item)" aria-hidden="true">
                        <i [class]="item.icon"></i>
                      </span>
                    }
                    <span class="flex-1 text-left">{{ item.label }}</span>
                    @if (item.shortcut) {
                      <span class="text-xs opacity-60">{{ item.shortcut }}</span>
                    }
                  </button>
                }
              }
            } @else {
              <ng-content select="[menuContent]" />
            }
            <!-- Optional projected row(s) rendered after the configured items
                 (e.g. a theme toggle). Clicking projected content stays inside the
                 container, so it does not auto-close the menu. -->
            <ng-content select="[menuFooter]" />
          </div>
        </div>
      }
    </div>
  `,
})
export class DropdownMenuComponent {
  private readonly router = inject(Router, { optional: true });

  /** Menu items to render */
  public items = input<DropdownMenuItemType[]>([]);

  /** Dropdown placement relative to trigger */
  public placement = input<'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'>('bottom-end');

  /** Tailwind width class for the dropdown panel */
  public menuWidth = input<string>('w-56');

  /** Whether the dropdown trigger is disabled */
  public disabled = input<boolean>(false);

  /** Emits when a menu item is clicked */
  public itemClick = output<DropdownMenuItemType>();

  /** Internal open/close state */
  public isOpen = signal<boolean>(false);

  /** Template reference to the container element */
  public container = viewChild<ElementRef>('container');

  /** Template reference to the panel element */
  public panel = viewChild<ElementRef>('panel');

  /** Computed CSS classes for the dropdown panel */
  public panelClasses = computed(() => {
    const base =
      'absolute z-50 mt-2 rounded-md bg-white dark:bg-surface-dark shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-white/10 focus:outline-none';
    const width = this.menuWidth();
    const position = this.getPositionClasses();

    return `${base} ${width} ${position}`;
  });

  /** Computed transform origin based on placement */
  public transformOrigin = computed(() => {
    const currentPlacement = this.placement();
    if (currentPlacement === 'bottom-start') return 'top left';
    if (currentPlacement === 'bottom-end') return 'top right';
    if (currentPlacement === 'top-start') return 'bottom left';
    if (currentPlacement === 'top-end') return 'bottom right';
    return 'top left';
  });

  /** Toggles the dropdown open/close */
  public toggle(): void {
    if (this.disabled()) return;
    this.isOpen.update((v) => !v);
  }

  /** Closes the dropdown */
  public close(): void {
    this.isOpen.set(false);
  }

  /** Space on the trigger toggles the menu (preventDefault stops page scroll). */
  public onTriggerSpace(event: Event): void {
    event.preventDefault();
    this.toggle();
  }

  /** Handles menu item click — executes command, navigates, emits event */
  public handleItemClick(item: DropdownMenuItemType): void {
    if (item.disabled) return;

    if (item.command && typeof item.command === 'function') {
      item.command();
    }

    if (item.routerLink && this.router) {
      const link = Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink];
      this.router.navigate(link);
    }

    this.itemClick.emit(item);
    this.close();
  }

  /** Closes dropdown when clicking outside */
  public onDocumentClick(event: MouseEvent): void {
    const containerEl = this.container()?.nativeElement;
    if (containerEl && !containerEl.contains(event.target as Node)) {
      this.close();
    }
  }

  /** Returns CSS classes for a menu item */
  public getItemClasses(item: DropdownMenuItemType): string {
    const base = 'w-full px-4 py-2 text-sm flex items-center gap-3 transition-colors duration-150';
    const disabled = 'opacity-50 cursor-not-allowed';
    const enabled =
      item.class ||
      'text-gray-900 hover:bg-gray-100 active:bg-gray-200 dark:text-white dark:hover:bg-surface-dark-muted dark:active:bg-dark-grey';

    return `${base} ${item.disabled ? disabled : enabled}`;
  }

  /** Returns CSS classes for a menu item icon */
  public getIconClasses(item: DropdownMenuItemType): string {
    return item.class || 'text-gray-500';
  }

  /** Resolves position classes from placement input */
  private getPositionClasses(): string {
    const currentPlacement = this.placement();
    if (currentPlacement === 'bottom-start') return 'left-0 top-full';
    if (currentPlacement === 'bottom-end') return 'right-0 top-full';
    if (currentPlacement === 'top-start') return 'left-0 bottom-full mb-2';
    if (currentPlacement === 'top-end') return 'right-0 bottom-full mb-2';
    return 'left-0 top-full';
  }
}
